import type {
    LogicActionNode,
    LogicNode,
    TradeLogicDiagnostic,
    TradeLogicDiagnosticSeverity,
    TradeLogicDiagnosticsState,
    TradeLogicJsonValue,
    TradeLogicNodeId
} from './types';
import { validateScheduleParameters } from './scheduleParameters';
import { flattenLogicNodes, hasImportedConditionExpression, isLogicContainerNode } from './treeOperations';

export const createEmptyDiagnosticsState = (): TradeLogicDiagnosticsState => ({
    items: []
});

export const diagnosticsByNodeId = (diagnostics: TradeLogicDiagnosticsState, nodeId: TradeLogicNodeId): TradeLogicDiagnostic[] =>
    diagnostics.items.filter((diagnostic) => diagnostic.nodeId === nodeId);

export const hasDiagnosticSeverity = (diagnostics: TradeLogicDiagnosticsState, severity: TradeLogicDiagnosticSeverity): boolean =>
    diagnostics.items.some((diagnostic) => diagnostic.severity === severity);

export const countDiagnosticsBySeverity = (diagnostics: TradeLogicDiagnosticsState): Record<TradeLogicDiagnosticSeverity, number> =>
    diagnostics.items.reduce<Record<TradeLogicDiagnosticSeverity, number>>(
        (counts, diagnostic) => ({
            ...counts,
            [diagnostic.severity]: counts[diagnostic.severity] + 1
        }),
        {
            error: 0,
            warning: 0,
            info: 0
        }
    );

const ACTION_KINDS_REQUIRING_QUANTITY = new Set(['enter']);

export const validateLogicTreeShape = (rootNode: LogicNode | null): TradeLogicDiagnostic[] => {
    if (!rootNode) {
        return [
            {
                id: 'logic-tree.empty-root',
                severity: 'error',
                code: 'logic-tree.empty-root',
                message: 'Trade logic has no root node.',
                source: 'local',
                path: []
            }
        ];
    }

    const seenNodeIds = new Set<TradeLogicNodeId>();
    const diagnostics: TradeLogicDiagnostic[] = [];
    const locations = flattenLogicNodes(rootNode);

    for (const location of locations) {
        const { node, path } = location;

        if (seenNodeIds.has(node.id)) {
            diagnostics.push({
                id: `logic-tree.duplicate-id.${node.id}.${path.join('.')}`,
                severity: 'error',
                code: 'logic-tree.duplicate-id',
                message: `Duplicate logic node id "${node.id}".`,
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        seenNodeIds.add(node.id);

        if (isLogicContainerNode(node) && node.kind === 'group' && node.operator === 'not' && node.children.length !== 1) {
            diagnostics.push({
                id: `logic-tree.invalid-not-group.${node.id}`,
                severity: 'error',
                code: 'logic-tree.invalid-not-group',
                message: 'NOT groups must contain exactly one child node.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (isLogicContainerNode(node) && node.children.length === 0) {
            const isRootContainer = path.length === 0;

            diagnostics.push({
                id: isRootContainer ? 'logic-tree.empty-root-container' : `logic-tree.empty-container.${node.id}`,
                severity: isRootContainer ? 'error' : 'warning',
                code: isRootContainer ? 'logic-tree.empty-root-container' : 'logic-tree.empty-container',
                message: isRootContainer ? 'Trade logic root container has no children.' : 'Container node has no children.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (node.kind === 'condition' && !node.featureRef && !node.templateKey && !hasImportedConditionExpression(node)) {
            diagnostics.push({
                id: `logic-tree.condition-missing-feature.${node.id}`,
                severity: 'error',
                code: 'logic-tree.condition-missing-feature',
                message: 'Condition node is missing a context feature or template.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (node.kind === 'condition' && !node.operator && !hasImportedConditionExpression(node)) {
            diagnostics.push({
                id: `logic-tree.condition-missing-operator.${node.id}`,
                severity: 'error',
                code: 'logic-tree.condition-missing-operator',
                message: 'Condition node is missing an operator.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (node.kind === 'action' && node.actionKind.trim().length === 0) {
            diagnostics.push({
                id: `logic-tree.action-missing-kind.${node.id}`,
                severity: 'error',
                code: 'logic-tree.action-missing-kind',
                message: 'Action node is missing an action kind.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (node.kind === 'action' && isActionMissingQuantity(node)) {
            diagnostics.push({
                id: `logic-tree.action-missing-quantity.${node.id}`,
                severity: 'error',
                code: 'logic-tree.action-missing-quantity',
                message: `${formatActionKind(node.actionKind)} actions must define a size fraction.`,
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (node.kind === 'schedule' && node.scheduleKind.trim().length === 0) {
            diagnostics.push({
                id: `logic-tree.schedule-missing-kind.${node.id}`,
                severity: 'error',
                code: 'logic-tree.schedule-missing-kind',
                message: 'Schedule node is missing a schedule kind.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }

        if (node.kind === 'schedule' && node.scheduleKind.trim().length > 0) {
            const scheduleParameterError = validateScheduleParameters(node);

            if (scheduleParameterError) {
                diagnostics.push({
                    id: `logic-tree.schedule-invalid-parameters.${node.id}`,
                    severity: 'error',
                    code: 'logic-tree.schedule-invalid-parameters',
                    message: scheduleParameterError,
                    source: 'local',
                    nodeId: node.id,
                    path
                });
            }
        }

        if (node.kind === 'reference' && `${node.referenceId}`.trim().length === 0) {
            diagnostics.push({
                id: `logic-tree.reference-missing-id.${node.id}`,
                severity: 'error',
                code: 'logic-tree.reference-missing-id',
                message: 'Reference node is missing a target.',
                source: 'local',
                nodeId: node.id,
                path
            });
        }
    }

    const hasEnabledSchedule = locations.some(({ node }) => node.enabled && node.kind === 'schedule');
    if (hasEnabledSchedule) {
        for (const { node, path } of locations) {
            if (node.enabled && node.kind === 'action' && node.parameters.priceExecution != null) {
                diagnostics.push({
                    id: `logic-tree.price-execution-schedule-conflict.${node.id}`,
                    severity: 'error',
                    code: 'logic-tree.price-execution-schedule-conflict',
                    message: 'Waiting-for-price entries must be armed by a completed bar and cannot be combined with a schedule.',
                    source: 'local',
                    nodeId: node.id,
                    path
                });
            }
        }
    }

    return diagnostics;
};

const isActionMissingQuantity = (node: LogicActionNode): boolean =>
    ACTION_KINDS_REQUIRING_QUANTITY.has(node.actionKind) && isEmptyJsonValue(node.parameters.quantity);

const isEmptyJsonValue = (value: TradeLogicJsonValue | undefined): boolean => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;

    return false;
};

const formatActionKind = (actionKind: string): string => {
    if (actionKind === 'partialExit') return 'Partial exit';

    return actionKind.charAt(0).toUpperCase() + actionKind.slice(1);
};
